import { datadogLogs } from '@datadog/browser-logs'
import axios, { AxiosResponse } from 'axios'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { isNil } from 'lodash'
import { SNOWPACK_PUBLIC_API_USERS_URL } from '@mth/envs'

export const loginUser = async (username: string, password: string) => {
  try {
    const url = `${SNOWPACK_PUBLIC_API_USERS_URL}/login`
    return await axios.post(url, { username, password }, { withCredentials: true })
  } catch (error) {
    const message = `Error logging in the user: ${username}`
    handleAxiosError(error, message)
    throw error
  }
}

let logoutPromise: Promise<AxiosResponse> | null = null

export const logoutUser = async (): Promise<AxiosResponse> => {
  if (logoutPromise) return logoutPromise

  const url = `${SNOWPACK_PUBLIC_API_USERS_URL}/logout`

  // Create a separate promise to assign to logoutPromise
  logoutPromise = axios
    .post(url, {}, { withCredentials: true })
    .catch((error) => {
      const message = 'Error logging out'
      handleAxiosError(error, message)
      throw new Error(message)
    })
    .finally(() => {
      logoutPromise = null // Reset *after* the promise has settled
    })

  return logoutPromise
}

const handleAxiosError = (error: unknown, message: string) => {
  if (axios.isAxiosError(error) && error.code === '401') {
    datadogLogs.logger.warn(message, {
      responseStatus: error.response?.status,
    })
  } else {
    datadogLogs.logger.error(message, { error })
  }
  throw error
}

export const validateSession = async () => {
  const currentPath = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  const expiration = searchParams.get('expiration')
  try {
    const token = localStorage.getItem('masquerade') ?? localStorage.getItem('JWT')
    if (isNil(token)) throw new Error('Token is empty')

    const url = `${SNOWPACK_PUBLIC_API_USERS_URL}/validate-session`

    await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (expiration) window.location.href = expiration
  } catch (error) {
    if (
      (axios.isAxiosError(error) && error.response?.status === 401) ||
      (error instanceof Error && error.message.includes('Token is empty'))
    ) {
      datadogLogs.logger.warn('Session expired.')
      const isLoginPage = ['/login', '/'].includes(currentPath)
      const shouldSkipRedirect = isLoginPage && expiration

      if (!shouldSkipRedirect) {
        await logoutUser()
        const reloadUrl = expiration ? currentPath : `/?expiration=${currentPath}`
        window.location.href = reloadUrl
      }
    } else {
      handleAxiosError(error, 'Error validating session')
    }
  }
}

const MILLISECONDS = 1000
const DEFAULT_LEEWAY_SECONDS = 60 // 1-minute buffer to handle clock drift

export const isTokenExpired = (token: string, leewaySeconds: number = DEFAULT_LEEWAY_SECONDS): boolean => {
  try {
    const decodedToken = jwtDecode<JwtPayload>(token)

    if (!decodedToken.exp) {
      console.warn('Token does not have an expiration (exp) field')
      return true // Consider tokens without exp as expired
    }

    const currentTime = Math.floor(Date.now() / MILLISECONDS)

    return decodedToken.exp < currentTime - leewaySeconds
  } catch (error) {
    console.error('Error validating token expiration:', error)
    return true // Malformed tokens are treated as expired
  }
}
